import { Page } from "@commerce/components/page";
import {
	OrderingSetup,
	OrderingSetupSkeleton,
} from "@commerce/orders/OrderingSetup";
import { isOrganizationAdmin } from "@repo/auth/lib/helper";
import { config } from "@repo/config";
import { AdminOnlyNotice } from "@saas/settings/components/SettingsLayout";
import { AsyncBoundary } from "@shared/components/AsyncBoundary";
import { orpc } from "@shared/lib/orpc";
import { dehydrateForLoader, getServerQueryClient } from "@shared/lib/server";
import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute(
	"/_saas/app/_org/$organizationSlug/orders/setup",
)({
	beforeLoad: ({ context }) => ({
		isOrganizationAdmin: isOrganizationAdmin(
			context.organization,
			context.session.user,
		),
	}),
	loader: async ({ context }) => {
		const organizationId = context.organization.id;
		const queryClient = getServerQueryClient();
		if (context.isOrganizationAdmin) {
			await Promise.all([
				queryClient
					.ensureQueryData(
						orpc.orders.settings.get.queryOptions({
							input: { organizationId },
						}),
					)
					.catch(() => null),
				queryClient
					.ensureQueryData(
						orpc.orders.backend.get.queryOptions({
							input: { organizationId },
						}),
					)
					.catch(() => null),
			]);
		}
		return {
			organizationId,
			dehydratedState: dehydrateForLoader(queryClient),
		};
	},
	// Shown inside the Shell while the loader runs on a client navigation.
	pendingComponent: OrderingSetupSkeleton,
	head: () => ({ meta: [{ title: `Ordering setup - ${config.appName}` }] }),
	component: OrderingSetupPage,
});

function OrderingSetupPage() {
	const { isOrganizationAdmin } = Route.useRouteContext();
	const { organizationId, dehydratedState } = Route.useLoaderData();
	if (!isOrganizationAdmin) {
		return (
			<Page width="narrow">
				<AdminOnlyNotice what="ordering setup" />
			</Page>
		);
	}
	return (
		<AsyncBoundary
			fallback={<OrderingSetupSkeleton />}
			dehydratedState={dehydratedState}
			errorFallback="inline"
		>
			<OrderingSetup organizationId={organizationId} />
		</AsyncBoundary>
	);
}
